/**
 * Fifty Rooms · recent searches (03 §H5).
 * - Saves the query of every /search page view into localStorage `fr_recent_searches`
 *   (max 5, newest first, case-insensitive de-duplication). Storage errors are ignored (private mode etc.).
 * - <fr-recent-searches> renders them as chips with a "Clear" button inside the search overlay's
 *   empty state; renders nothing (stays hidden) when the list is empty.
 */

const STORAGE_KEY = 'fr_recent_searches';
const MAX_ITEMS = 5;
const MAX_LENGTH = 80;

/** @returns {string[]} */
export function readRecentSearches() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list.filter((item) => typeof item === 'string' && item.trim()).slice(0, MAX_ITEMS) : [];
  } catch (error) {
    return [];
  }
}

/** @param {string[]} list */
function writeRecentSearches(list) {
  try {
    if (list.length === 0) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_ITEMS)));
    }
  } catch (error) {
    /* storage unavailable: nothing to do */
  }
}

/** @param {string} term */
export function addRecentSearch(term) {
  const clean = String(term || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_LENGTH);
  if (!clean) return;
  const lower = clean.toLowerCase();
  const list = readRecentSearches().filter((item) => item.toLowerCase() !== lower);
  list.unshift(clean);
  writeRecentSearches(list);
}

export function clearRecentSearches() {
  writeRecentSearches([]);
}

/* Record the query when the search results page is shown (covers the form, chips, "View all" and Enter). */
function recordCurrentSearch() {
  try {
    const url = new URL(window.location.href);
    if (!/\/search\/?$/.test(url.pathname)) return;
    const query = url.searchParams.get('q');
    if (query) addRecentSearch(query);
  } catch (error) {
    /* ignore */
  }
}

class FrRecentSearches extends HTMLElement {
  connectedCallback() {
    this.render();
  }

  render() {
    const list = readRecentSearches();
    this.replaceChildren();

    if (list.length === 0) {
      this.hidden = true;
      return;
    }

    const searchUrl = this.dataset.searchUrl || '/search';
    const titleId = `FrRecentSearches-${Math.random().toString(36).slice(2, 8)}`;

    const header = document.createElement('div');
    header.className = 'fr-recent-searches__header';

    const title = document.createElement('h4');
    title.className = 'fr-search-label';
    title.id = titleId;
    title.textContent = this.dataset.title || 'Recent searches';

    const clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'fr-recent-searches__clear button-unstyled';
    clear.textContent = this.dataset.clearLabel || 'Clear';
    clear.setAttribute('data-fr-recent-clear', '');
    clear.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      clearRecentSearches();
      this.render();
      const input = this.closest('predictive-search-component')?.querySelector('input[name="q"]');
      if (input instanceof HTMLElement) input.focus();
    });

    header.append(title, clear);

    const ul = document.createElement('ul');
    ul.className = 'fr-search-chips__list list-unstyled';
    ul.setAttribute('role', 'list');
    ul.setAttribute('aria-labelledby', titleId);

    for (const term of list) {
      const url = new URL(searchUrl, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');

      const li = document.createElement('li');
      const a = document.createElement('a');
      a.className = 'fr-chip fr-chip--recent';
      a.href = url.pathname + url.search;
      a.textContent = term;
      li.append(a);
      ul.append(li);
    }

    this.append(header, ul);
    this.hidden = false;
  }
}

if (!customElements.get('fr-recent-searches')) {
  customElements.define('fr-recent-searches', FrRecentSearches);
}

recordCurrentSearch();
